import Button from "@mui/material/Button";
import { fetchAllLeaguesBySport } from "../../../../api/leagues";
import { useCallback, useEffect, useState } from "react";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import { Box } from "@mui/system";
import { ArrowDropUp } from "@mui/icons-material";
import ConfigurationApi from "../configurationApiBlock";
import { ConfigureSources } from "../configureSources";
import { getAllCompetitionsByLeague } from "../../../../api/competitions";
import ConfigureNodeWrapper from "../configureNode/ConfigureNodeWrapper";

export default function ConfigureList({
  projectId,
  sport,
  id,
  onCloseDialog,
  onSubmit,
  initialHash,
  setConfiguration,
  sources,
}) {
  const [hash, setHash] = useState(null);
  const [isInited, setIsInited] = useState(false);
  const [treeHash, setTreeHash] = useState([]);
  const [hashBySource, setHashBySource] = useState({});
  const [isApiWindowOpen, setIsApiWindowOpen] = useState(false);

  const uncheckAllChildren = useCallback(
    (level, id) => {
      const key = `${level}-${id}`;
      const keys = Object.values(hash)
        .filter((item) => item.level === level + 1 && item.parentId === id)
        .reduce(
          (a, item) => [...uncheckAllChildren(level + 1, item.id), ...a],
          []
        );
      const result = [...keys, key];
      return result;
    },
    [hash]
  );

  const getEntityType = useCallback((item) => {
    const level = item.level;
    if (level === 2) {
      return "League";
    } else if (level === 3) {
      return "Competition";
    }
  }, []);

  const handleButtonClick = useCallback(async () => {
    const data = Object.values(hash).map((item) => ({
      entityId: item.id,
      entityType: getEntityType(item),
      displayOrder: item.displayOrder || null,
      priority: item.priority
    }));
    await setConfiguration(projectId, sport.id, { items: data });
    await onSubmit();
    onCloseDialog();
  }, [
    hash,
    getEntityType,
    projectId,
    sport,
    onCloseDialog,
    onSubmit,
    setConfiguration,
  ]);

  const handleHashChange = useCallback((items, state) => {
    let newHash = { ...hash };
    items.forEach(({ level, id, parentId, displayOrder, priority }) => {
      const key = `${level}-${id}`;
      if (!state) {
        const keys = uncheckAllChildren(level, id);
        keys.forEach((key) => delete newHash[key]);
      } else {
        newHash = {
          ...newHash,
          [key]: { level, id, parentId, displayOrder, priority },
        };
      }
    });
    setHash(newHash);
  },
    [hash, uncheckAllChildren]
  );

  const setupData = useCallback(async () => {
    if (!sources) {
      return;
    }
    const hashBySource = sources.reduce((a, c) => {
      a[c.id] = {};
      return a;
    }, {});

    const sourceIdParams = sources.map((item) => ({ key: 'sources', value: item.id }));
    const params = [
      {
        key: "sportId",
        value: sport.id,
      },
      ...sourceIdParams,
    ];
    const [leagues, competitions] = await Promise.all([fetchAllLeaguesBySport(params), getAllCompetitionsByLeague(params)]);

    leagues.forEach((item) => {
      if (!hashBySource[item.source]) {
        hashBySource[item.source] = {};
      }
      hashBySource[item.source][item.id] = { ...item };
    });

    const filteredCompetitions = competitions.filter((item) => !!hashBySource[item.source][item.leagueId]);

    filteredCompetitions.forEach((item) => {
      if (!hashBySource[item.source][item.leagueId].children) {
        hashBySource[item.source][item.leagueId].children = [];
      }
      hashBySource[item.source][item.leagueId].children.push(item);
    });

    const treeHash = leagues.reduce((a, c) => {
      if (!c.unionKey) {
        a[c.id] = { ...c, displayOrder: hash[`2-${c.id}`]?.displayOrder, children: [] };
        return a;
      }
      if (!a[c.unionKey]) {
        a[c.unionKey] = { items: [], children: [] };
      }
      a[c.unionKey].items.push({ ...c, displayOrder: hash[`2-${c.id}`]?.displayOrder, priority: hash[`2-${c.id}`]?.priority || a[c.unionKey].items.length + 1 });
      return a;
    }, {});

    const mergedCompetitionsChildren = {};

    competitions.flat(1).forEach((item) => {
      if (item.leagueUnionKey) {
        if (item.unionKey) {
          if (!mergedCompetitionsChildren[item.leagueUnionKey]) {
            mergedCompetitionsChildren[item.leagueUnionKey] = {};
          }
          if (!mergedCompetitionsChildren[item.leagueUnionKey][item.unionKey]) {
            mergedCompetitionsChildren[item.leagueUnionKey][item.unionKey] = [];
          }
          mergedCompetitionsChildren[item.leagueUnionKey][item.unionKey].push({ ...item, displayOrder: hash[`3-${item.id}`]?.displayOrder, priority: hash[`3-${item.id}`]?.priority || mergedCompetitionsChildren[item.leagueUnionKey][item.unionKey].length + 1 });
        } else {
          treeHash[item.leagueUnionKey].children.push({ ...item, displayOrder: hash[`3-${item.id}`]?.displayOrder });
        }
      } else {
        treeHash[item.leagueId].children.push({ ...item, displayOrder: hash[`3-${item.id}`]?.displayOrder });
      }
    });

    Object.keys(mergedCompetitionsChildren).forEach((leagueUnionKey) => {
      Object.keys(mergedCompetitionsChildren[leagueUnionKey]).forEach((unionKey) => {
        treeHash[leagueUnionKey].children.push({ items: mergedCompetitionsChildren[leagueUnionKey][unionKey] });
      })
    });

    setTreeHash(treeHash);
    setHashBySource(hashBySource);
    setIsInited(true);
  }, [sport, sources, hash]);


  useEffect(() => {
    if (!isInited) {
      setHash(initialHash);
      if (hash) {
        setupData();
      }
    }
  }, [id, projectId, setupData, initialHash, isInited, hash]);

  return (
    <Box sx={{ padding: "25px", display: 'flex', flexDirection: 'column', gap: '25px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="contained"
          color={"success"}
          onClick={handleButtonClick}
        >
          {"Save"}
        </Button>
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <Button
            variant="contained"
            color={"info"}
            onClick={() => setIsApiWindowOpen(!isApiWindowOpen)}
          >
            {"api's configuration"}
            {isApiWindowOpen ? <ArrowDropUp /> : <ArrowDropDownIcon />}
          </Button>
        </Box>
      </Box>
      {isApiWindowOpen && <ConfigurationApi sources={sources} sport={sport} onSave={setupData} onClose={() => setIsApiWindowOpen(!isApiWindowOpen)} />}
      <Box sx={{ background: "#f9f9f9", }}>
        <h2 style={{ textAlign: 'center' }}>CONFIGURATION</h2>
        <Box sx={{ display: 'flex' }}>
          <ConfigureNodeWrapper treeHash={treeHash} hash={hash} onHashChange={handleHashChange} />
          <ConfigureSources
            hash={hash}
            hashBySource={hashBySource}
            onHashChange={handleHashChange}
            sport={sport}
            sources={sources}
          />
        </Box>
      </Box>
    </Box>
  );
}
